import SearchContainer from '@/components/Approval/RightContainer/SearchContainer';
import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { SearchData } from '@/types/api';
import NewCustomInput from '@/components/CustomInput';
import useSearch from '@/components/Approval/RightContainer/SearchApproval/hooks/useSearch';
import { ContentsLayout } from '@/components/Approval/RightContainer/SearchApproval/styles';
import RequestSearch from '@/components/Approval/RightContainer/SearchApproval/RequestSearch';
import { useDebounce } from 'usehooks-ts';
import {
  useGetFilterRecommend,
  useGetNewTalkFlowList,
  useRecentReadApproval,
} from '@/hooks/apiHooks';
import { INIT_WEB_DOCUMENT_FILTER_STATE } from '@/recoil/init_data';
import TalkFLowModal, {
  TalkFlowModalRef,
} from '@/components/Record/TalkFLowModal';
import SortPopover from '@/components/Approval/@components/SortPopover';
import { useAtom } from 'jotai';
import { documentFilterState } from '@/recoil/atom';
import { EventPageAreaName } from '@/constant/gtm';
import { Stack } from '@mui/material';
import RecentViewedItem from '@/components/Work/WorkSNB/SNBRecent/RecentViewedItem';
import {
  EMPTY_SUBJECT,
  getTalkFlowDisplaySubject,
} from '@/components/Record/util/function';

type P = {
  isTablet: boolean;
  /** 정렬 드롭다운을 숨김(상단 필터바에서는 검색만, 정렬은 SubHeader 에 별도 배치) */
  hideSort?: boolean;
};

export const documentSortList: { value: string; title: string }[] = [
  { value: 'createdAt', title: '최근 등록순' },
  { value: 'updatedAt', title: '최근 업데이트순' },
];

const SearchDocument = ({ isTablet, hideSort }: P) => {
  const [filters, setFilters] = useAtom(documentFilterState);
  const methods = useFormContext<SearchData>();
  const { watch } = methods;
  const searchString = watch('keyword');
  const debouncedValue = useDebounce(searchString, 500);
  const searchMode = debouncedValue.length > 1;

  const { data: keywordData, mutate: keywordMutate } = useGetFilterRecommend();
  const keywordDataLength = (keywordData && keywordData.keywords?.length) || 0;

  const { data: searchData, isLoading } = useGetNewTalkFlowList({
    ...INIT_WEB_DOCUMENT_FILTER_STATE,
    documentTypes: '',
    createdBy: [],
    internalParticipants: undefined,
    listSize: 5,
    keyword: searchMode ? debouncedValue : '',
    searchFields: watch('searchFields'),
  });

  const haveSearchData = searchData && searchData.data.length > 0;
  const { data: recentSearchData } = useRecentReadApproval();

  const recordModal = useRef<TalkFlowModalRef>(null);
  const openTalkFlowModal = (docId: string) => {
    recordModal.current?.open(docId);
  };

  const onSubmit = ({ keyword }: SearchData) => {
    setFilters({
      ...filters,
      keyword: keyword,
      searchFields: 'subject',
      currentPage: 1,
    });
  };

  const {
    isFocus,
    inputRef,
    menuRef,
    t,
    handleFocus,
    handleKeyUp,
    focusedIndex,
    handleSubmit,
    setItems,
    handleOpen,
    handleFocusBlur,
  } = useSearch({
    pageArea: EventPageAreaName.WORK_DOCUMENT,
    areaName: 'Document 목록',
    onSubmit,
    keywordDataLength,
    searchDocIdData: searchData?.data.map((res) => res.id),
    recentDocIDData: recentSearchData?.map((res) => res.docId),
    keywordData: keywordData?.keywords,
    openApprovalModal: openTalkFlowModal,
  });

  useEffect(() => {
    setItems(
      searchMode
        ? searchData?.data.length ?? 0
        : (keywordData?.keywords?.length ?? 0) +
            (recentSearchData?.length ?? 0),
    );
  }, [keywordData, recentSearchData, searchData, searchMode]);

  const handleSelected = (value: string) => {
    setFilters({ ...filters, sortType: value });
  };

  const handleDocumentOpen = (id: string) => {
    handleOpen(id);
    handleSubmit({ keyword: debouncedValue });
  };

  return (
    <SearchContainer
      ref={menuRef}
      isTablet={isTablet}
      searchComponent={
        <>
          <NewCustomInput
            ref={inputRef}
            isFocus={isFocus}
            onSubmit={handleSubmit}
            placeHolder={t('제목을 입력해 주세요.')}
            handleFocus={handleFocus}
            handleKeyUp={handleKeyUp}
            style={{
              width: '240px',
              height: '36px',
              alignItems: 'center',
            }}
          />
          <ContentsLayout isFocus={isFocus && debouncedValue.length > 1}>
            {searchMode ? (
              haveSearchData ? (
                <Stack gap={'4px'} sx={{ padding: '16px 12px' }}>
                  {searchData.data.map((item, index) => (
                    <RecentViewedItem
                      key={item.id}
                      id={item.id}
                      title={getTalkFlowDisplaySubject(
                        item.subject,
                        item.suggestedSubject,
                        EMPTY_SUBJECT,
                      )}
                      authorName={item.createdBy.name}
                      authorPosition={item.createdBy.positionName}
                      viewedDate={item.createdAt}
                      onClick={() => handleDocumentOpen(item.id)}
                      isFocused={focusedIndex === index}
                    />
                  ))}
                </Stack>
              ) : (
                <RequestSearch
                  title={t(
                    '조건에 해당하는 문서가 없어요.\n검색어를 확인해 주세요.',
                  )}
                  isLoading={isLoading}
                />
              )
            ) : (
              <></>
              // <InitSearchView
              //   keywordData={keywordData?.keywords}
              //   recentSearchData={recentSearchData}
              //   handleSearch={handleSubmit}
              //   handleOpen={handleOpen}
              //   mutate={keywordMutate}
              //   focusedIndex={focusedIndex}
              // />
            )}
          </ContentsLayout>
        </>
      }
      sortComponent={
        hideSort ? undefined : (
          <SortPopover
            list={documentSortList}
            handleSelected={handleSelected}
          />
        )
      }
      modalComponent={
        <TalkFLowModal ref={recordModal} onAfterClose={handleFocusBlur} />
      }
    />
  );
};
export default SearchDocument;
